/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { debounce } from "lodash";
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { SearchResponse } from "typesense/lib/Typesense/Documents";

import ContactCard from "../../components/ContactCard";
import HelperMessage from "../../components/HelperMessage";
import { Loader, LoadingBox } from "../../components/Loader";
import Pagination, { PaginationCounter, PaginationDetails } from "../../components/Pagination";
import { useAppDispatch } from "../../store/rootStore";
import TestIds from "../../testIds";
import { PAGINATION_DATA_LIMIT, SortingOptions } from "../../util/consts";
import { generateImageUrl } from "../../util/helpers";
import { error as logError } from "../../util/log";
import { usePreviousPage } from "../../util/usePreviousPage";
import { RelationshipOwner, selectCurrentFarmerAccount } from "../azure-b2c/storage";
import { selectCurrentCart } from "../cart/storage/cart.slice";
import { CartDeliveryMode } from "../cart/storage/cart.types";
import { ShopProductCardSwitch } from "../products/components/ProductCard";
import { SelectedFiltersType } from "../products/components/ProductFilter";
import ProductList from "../products/components/ProductList";
import ProductListContainer from "../products/pages/ProductsListContainer";
import { ActivePrice, Product } from "../products/storage";
import { StocksSlice } from "../products/storage/stocks.slice";
import { selectStores } from "../stores/storage/stores.slice";
import { locationToSiteId } from "../stores/storage/utils";

import { getCategories, multiSearchWithActivePrices } from "./api";
import { CategoryResponse, TypesenseProduct } from "./types";

export const sortOptions = [
  SortingOptions.BestMatch,
  SortingOptions.PriceAscending,
  SortingOptions.PriceDescending,
];

interface TypesenseSearchProps extends Record<string, unknown> {
  accountNumber: string | undefined;
  setCategoriesCallback: (categories: string[]) => void;
  data: Product[];
  handleClearFilters: () => void;
  isFilterApplied: boolean;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  selectedFilters: SelectedFiltersType;
  currentPage: number;
  relationshipOwner: RelationshipOwner;
  /** Base path used for pagination links. Defaults to '/products'. */
  path?: string;
}

const TypesenseSearch: FC<TypesenseSearchProps> = ({
  accountNumber,
  setCategoriesCallback,
  data,
  handleClearFilters,
  isFilterApplied,
  sortBy,
  selectedFilters,
  currentPage,
  relationshipOwner,
  setSortBy,
  path = "/products",
}) => {
  const dispatch = useAppDispatch();
  const [hits, setHits] = useState<SearchResponse<TypesenseProduct & ActivePrice>>();
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const cart = useSelector(selectCurrentCart);
  const stores = useSelector(selectStores);
  const selectedAccount = useSelector(selectCurrentFarmerAccount);

  const showInStockMessageHelper = useMemo(() => {
    return cart?.cartDeliveryMode === CartDeliveryMode.DELIVERY && selectedFilters.inStock.value;
  }, [selectedFilters.inStock.value, cart?.cartDeliveryMode]);

  const account = useSelector(selectCurrentFarmerAccount);
  const isTfo = account?.hasTfoAccess;

  useEffect(() => {
    if (categories) {
      setCategoriesCallback(categories.map((e) => e.value));
    }
  }, [categories]);

  const { previousPage } = usePreviousPage();
  const restoreScrollPosition = () => {
    setTimeout(() => {
      const element = document.getElementById(TestIds.pages.mainScrollContainer);
      if (
        element &&
        sessionStorage.scrollYPositionOnProducts &&
        previousPage.startsWith("/products/")
      ) {
        element.scrollTo({
          top: parseInt(sessionStorage.scrollYPositionOnProducts) ?? 0,
          behavior: "auto",
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (!loading) {
      restoreScrollPosition();
    }
  }, [loading]);

  const paginationCounter = useMemo(() => {
    const count = hits?.found ?? 0;
    const total = hits?.found;
    const currentPage = hits?.page ?? 0;

    return (
      <PaginationCounter
        data-testid={TestIds.components.pagination.number}
        label="products"
        itemsPerPage={PAGINATION_DATA_LIMIT}
        count={count}
        total={total}
        currentPage={currentPage}
      />
    );
  }, [hits, currentPage, selectedFilters, isFilterApplied]);

  const pagination = useMemo(() => {
    const total = hits?.found;
    const _currentPage = hits?.page ?? currentPage;

    if (hits && total && total > PAGINATION_DATA_LIMIT) {
      return (
        <Pagination
          path={path}
          page={_currentPage}
          count={Math.ceil(total / PAGINATION_DATA_LIMIT)}
        />
      );
    }
  }, [hits]);

  useEffect(() => {
    (async () => {
      const { categories } = await getCategories();
      const filteredCategories = categories.filter((x) => {
        if (x.value.startsWith("Seed")) {
          return true;
        }
        return true;
      });
      setCategories(filteredCategories);
      if (!sortOptions.includes(sortBy as SortingOptions)) {
        setSortBy(sortOptions[0]);
      }
    })();
  }, []);

  const fetchResults = async (
    selectedFilters: SelectedFiltersType,
    sortBy: string,
    currentPage: number
  ) => {
    try {
      setLoading(true);
      // multiSearchWithActivePrice if you want to wait for results in one go
      const branchId = locationToSiteId(selectedFilters.storeId.value ?? "");
      const { searchResults } = await multiSearchWithActivePrices({
        accountNumber,
        currentPage,
        sortBy,
        selectedFilters,
        branchId,
      });
      setHits(searchResults);
      dispatch(
        StocksSlice.actions.addSearchResults(
          searchResults.hits?.map((hit) => {
            return { ...hit.document, lifeCycleState: hit.document.lifecycle };
          }) ?? []
        )
      );
    } catch (err) {
      logError("Error fetching results:", err);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchResults = useCallback(
    debounce((selectedFilters, sortBy, currentPage) => {
      fetchResults(selectedFilters, sortBy, currentPage);
    }, 100),
    [
      selectedFilters.category.value,
      selectedFilters.searchText.value,
      selectedFilters.dateRange.value,
      selectedFilters.priceRange.value,
      selectedFilters.inStock.value,
      selectedFilters.storeId.value,
      selectedFilters.promotions?.value,
      sortBy,
      currentPage,
    ]
  );

  useEffect(() => {
    debouncedFetchResults(selectedFilters, sortBy, currentPage);
    return () => {
      debouncedFetchResults.cancel();
    };
  }, [debouncedFetchResults]);

  const productsToRender = useMemo(() => {
    if (hits) {
      const hitsToRender = hits;
      return hitsToRender?.hits?.map((hit) => hit.document);
    }
  }, [hits]);

  if (!hits && !loading) {
    return null;
  }
  if (loading) {
    return (
      <LoadingBox>
        <Loader
          title="Loading your products"
          type="inline-flex"
          sx={{ marginTop: "12px" }}
          data-testid={TestIds.components.loader.searchProduct}
        />
      </LoadingBox>
    );
  }

  const showHelperMessage =
    !!isTfo &&
    showInStockMessageHelper &&
    stores.find((e) => e.inventorySiteId === account?.homeBranchId) !== undefined;
  const helperTitle = "You currently have Delivery selected";
  const homeBranch = stores.find((e) => e.inventorySiteId === account?.homeBranchId)?.orgUnitName;
  const helperMessageBody = `Stock levels shown are for the home branch: ${homeBranch}\nProducts can still be ordered in or fulfilled from other stores.`;

  return (
    <ProductListContainer>
      <PaginationDetails
        onClear={handleClearFilters}
        showClear={isFilterApplied}
        paginationCounter={paginationCounter}
        sorting={{
          value: sortBy,
          onChange: setSortBy,
          options: sortOptions,
        }}
      />
      <HelperMessage show={showHelperMessage} title={helperTitle} body={helperMessageBody} />
      {productsToRender && (
        <ProductList<TypesenseProduct & ActivePrice>
          items={productsToRender}
          renderListItem={(e) => {
            const hit = e;
            const match = data.find((e) => e.productId === hit.id, 10);
            return (
              <ShopProductCardSwitch
                key={hit.id}
                isPastProduct={false}
                item={{
                  category: hit.l1_category,
                  subCategory: hit.l2_category,
                  name: hit.name,
                  description: hit.name,
                  lifeCycleState: hit.lifecycle,
                  masterProductId: hit.id,
                  productId: hit.productId,
                  orders: [...(match?.orders ?? [])],
                  itemNumber: hit.item_id,
                  imageUrl: generateImageUrl(hit.item_id),
                  basePriceExcGst: hit.basePriceExcGst,
                  basePriceIncGst: hit.price,
                  discountedPriceExcGst: hit.discountedPriceExcGst,
                  discountedPriceIncGst: hit.discountedPriceIncGst,
                  hasVariants: hit.has_variants,
                  isPriceNegotiable: hit.price_negotiable,
                  stockAvailabilities: hit.stockAvailabilities,
                  stockAvailability: hit.stock,
                  promotions: hit.promotions?.map((p) => ({
                    id: p.id,
                    name: p.name,
                    type: p.type,
                  })),
                  priceBreaks: hit.priceBreaks,
                }}
                enableQuote={!!selectedAccount?.relationshipOwner?.fullName}
              />
            );
          }}
        />
      )}
      {pagination}
      {hits && hits.found < 25 && (
        <ContactCard
          title={`Can't find what you're looking for?\nContact your Farmlands representative.`}
          sx={{ minWidth: "100%" }}
          {...relationshipOwner}
        />
      )}
    </ProductListContainer>
  );
};

export default TypesenseSearch;
